import * as React from "react"
import {
  ArrowLeftRightIcon,
  HandCoinsIcon,
  HeartHandshakeIcon,
  PieChartIcon,
  ReceiptTextIcon,
  type LucideIcon,
} from "lucide-react"

import { FeatureFlag } from "@ibada/shared-types/feature-flags"

import { useTranslations } from "@/i18n"
import { useFeatureFlag } from "@/shared/feature-flags"
import { useModulePermission } from "@/features/auth/hooks/use-module-permission"
import { WorkspaceAccessModule } from "@/features/auth/model/auth-types"
import type { WidgetCreateAction } from "@ibada/dashboard-engine"

import { useFinanceOverviewScope } from "@/features/finance/overview/context/finance-overview-context"
import { OverviewRoutes, useOverviewNav } from "@/features/finance/overview/lib/overview-nav"

/** Create shortcuts a widget can request; labels/icons/routes are resolved centrally (reusing the global
 *  "+" create vocabulary) and gated by the same module permissions + parish context. */
export type WidgetCreateKey = "income" | "expense" | "transfer" | "pledge" | "budget"

interface CreateSpec {
  labelKey: string
  icon: LucideIcon
  route: string
  show: boolean
}

/**
 * Resolve a widget's requested create shortcuts into permission-gated menu actions. Each navigates to the
 * existing create flow (no duplicated business rules). Only actions the user may perform in the current
 * branch/parish context are returned, in the order requested.
 */
export function useWidgetCreateActions(keys: readonly WidgetCreateKey[]): WidgetCreateAction[] {
  const t = useTranslations("globalCreate")
  const scope = useFinanceOverviewScope()
  const go = useOverviewNav()
  const budgeting = useModulePermission(WorkspaceAccessModule.BUDGETING)
  // Paused for V1 — a create shortcut must not open a feature that is switched off.
  const budgetingEnabled = useFeatureFlag(FeatureFlag.Budgeting)
  const pledgesEnabled = useFeatureFlag(FeatureFlag.Pledges)
  const isParish = scope.context === "parish"
  // No create shortcuts on the consolidated board. These WRITE, and the route can only carry the ACTIVE
  // branch — so "record an offering" launched from a church-wide figure would post into whichever branch
  // happened to be selected. A wrong destination for a read is confusing; for a posting it is a bad entry.
  const financeEdit = scope.canEdit

  return React.useMemo(() => {
    const specs: Record<WidgetCreateKey, CreateSpec> = {
      income: { labelKey: "items.offering", icon: HandCoinsIcon, route: OverviewRoutes.createIncome, show: financeEdit },
      expense: { labelKey: "items.expense", icon: ReceiptTextIcon, route: OverviewRoutes.createExpense, show: financeEdit },
      transfer: { labelKey: "items.transfer", icon: ArrowLeftRightIcon, route: OverviewRoutes.createTransfer, show: financeEdit },
      pledge: { labelKey: "items.pledge", icon: HeartHandshakeIcon, route: OverviewRoutes.createPledge, show: pledgesEnabled && financeEdit && !isParish },
      budget: {
        labelKey: "items.budget",
        icon: PieChartIcon,
        route: OverviewRoutes.createBudget,
        // `scope.canEdit` as well, exactly as the four above. Its own module permission is not enough: a
        // parish reader inspecting a branch may hold budgeting rights and still have no business opening a
        // create flow scoped to the branch they are reading.
        show: budgetingEnabled && budgeting.canEdit && scope.canEdit,
      },
    }
    return keys.reduce<WidgetCreateAction[]>((acc, key) => {
      const spec = specs[key]
      // Asked per destination rather than once for the set: an action whose route cannot honour the branch
      // filter is dropped rather than offered, so a new create shortcut is safe without anyone remembering.
      if (spec?.show && go.canNavigateTo(spec.route)) {
        const Icon = spec.icon
        acc.push({
          key,
          label: t(spec.labelKey),
          icon: <Icon className="size-4" aria-hidden="true" />,
          onSelect: () => go(spec.route),
        })
      }
      return acc
    }, [])
  }, [keys, t, go, financeEdit, isParish, budgeting.canEdit, budgetingEnabled, pledgesEnabled, scope.canEdit])
}
